// ----------------------------------------------------------------------

const ROOTS = {
  AUTH: '/auth',
  DASHBOARD: '/dashboard',
  VOTERS: '/voters',
  TEAMS: '/teams',
  USERS: '/users',
  FINANCES: '/finances',
  BILLING: '/billing',
  ASSIGNED_TASKS: '/assigned-tasks',
};

// ----------------------------------------------------------------------

export const paths = {
  // AUTH
  auth: {
    login: `${ROOTS.AUTH}/login`,
  },
  // DASHBOARD
  dashboard: {
    root: ROOTS.DASHBOARD,
    dailyBrief: `${ROOTS.DASHBOARD}/daily-brief`,
    geographicalOverview: `${ROOTS.DASHBOARD}/geographical-overview`,
    voteTracking: `${ROOTS.DASHBOARD}/vote-tracking`,
    tasks: `/:campaignId/tasks`,
    results: `/:campaignId/results`,
    volunteers: `/:campaignId/volunteers`,
    adminUsers: `/dashboard/users`,
    tags: `/dashboard/tags`,
    tagsUpload: `/dashboard/tags/upload`,
    campaigns: `/dashboard/campaigns`,
    teams: {
      root: '/dashboard/teams',
      create: withParams('/dashboard/teams/:orgId/create'),
    },
  },
  campaign: {
    create: withParams('/campaigns/:organizationId/create'),
    edit: withParams('/campaigns/:campaignId/:organizationId/edit'),
    tasks: withParams(`/campaigns/:campaignId/tasks`),
    results: withParams(`/campaigns/:campaignId/results`),
    volunteers: withParams(`/campaigns/:campaignId/volunteers`),
    manualBatchTasks: withParams(`/campaigns/manual-text-batch/:batchId/tasks`),
  },
  // National tracking console. Superadmin-only, and deliberately NOT nested under
  // /superadmin: it spans every organisation's voters rather than administering one,
  // so it is a peer of the other analytics sections, not an admin tool.
  national: {
    root: '/national',
    districtOverview: '/national/district-overview',
    priorityDistricts: '/national/priority-districts',
    fieldContact: '/national/field-contact',
  },
  // Campaign planner. Superadmin-only for now, and a peer of /national for the same
  // reason: it plans a geography rather than administering an organisation.
  planner: {
    root: '/planner',
  },
  superadmin: {
    root: '/superadmin',
    automatedVoterReport: '/superadmin/automated-voter-report',
    orgs: '/superadmin/organizations',
    number_managment: '/superadmin/phone-numbers',
    adminUsers: '/superadmin/users',
    status: '/superadmin/status',
    announcements: '/superadmin/announcements',
    search: '/superadmin/search',
    voter_analytics: {
      root: '/superadmin/voter-analytics',
      geographical_summary: '/superadmin/voter-analytics/geographical-summary',
      new_voter_registration_summary: '/superadmin/voter-analytics/new-voter-registration-summary',
      potential_voters_remaining: '/superadmin/voter-analytics/potential-voters-remaining',
      voter_turnout_modeling: '/superadmin/voter-analytics/voter-turnout-modeling',
      individual_analysis: '/superadmin/voter-analytics/individual-analysis',
      election_history: '/superadmin/voter-analytics/election-history',
      voter_list: '/superadmin/voter-analytics/voter-list',
      absentee_tracker: '/superadmin/voter-analytics/absentee-tracker',
    },
  },
  reports: {
    root: '/reports',
    tasks: '/reports/tasks',
    organizations: '/reports/organizations',
    campaigns: '/reports/campaigns',
    volunteers: '/reports/volunteers',
    teams: '/reports/teams',
    activity: '/reports/activity',
    activityMap: '/reports/activity-map',
    voters: '/reports/voters',
    doNotContact: '/reports/do-not-contact',
    newMovers: '/reports/new-movers',
    districtLeanings: '/reports/district-leanings',
    absentee: '/reports/absentee',
    pinnedLocations: '/reports/pinned-locations',
    replies: '/reports/replies',
    voicemailReplies: '/reports/voicemail-replies',
    bad_numbers: '/reports/bad-numbers',
    potentialCandidates: '/reports/potential-candidates',
    manualActivities: '/reports/manual-activities',
    voterContactUpdates: '/reports/voter-contact-updates',
  },
  questionBank: {
    root: '/question-banks-and-surveys',
    questionBankCreate: `/question-bank/create`,
    questionBankAndSmsSurveyResults: `/question-banks-and-surveys/results`,
  },
  smsSurveys: {
    root: '/sms-surveys',
    smsSurveyCreate: `/sms-surveys/create`,
  },
  assignedTasks: {
    root: ROOTS.ASSIGNED_TASKS,
    session: (taskId) => `${ROOTS.ASSIGNED_TASKS}/${taskId}/session`,
  },
  voters: {
    root: ROOTS.VOTERS,
    legRoot: '/constituents',
    // Rebuilt search screen — superadmin-only while it's being validated. Its own route (rather
    // than a toggle on `root`) so a shared link carries the experience along with its filters.
    search: `${ROOTS.VOTERS}/search`,
    legSearch: '/constituents/search',
    // The classic list, kept as an opt-out from the search screen; nothing else links here.
    classic: `${ROOTS.VOTERS}/classic`,
    legClassic: '/constituents/classic',
    details: withParams(`${ROOTS.VOTERS}/:state/:id`),
    legDetails: withParams(`/constituents/:state/:id`),
  },
  users: {
    root: ROOTS.USERS,
    create: `${ROOTS.USERS}/create`,
    edit: withParams(`${ROOTS.USERS}/:orgId/:userId/edit`),
    profile: withParams(`${ROOTS.USERS}/:userId`),
  },
  teams: {
    root: ROOTS.TEAMS,
    orgRoot: withParams(`${ROOTS.TEAMS}/:orgId`),
    create: withParams(`${ROOTS.TEAMS}/:orgId/create`),
    edit: withParams(`${ROOTS.TEAMS}/:orgId/:teamId/edit`),
  },
  help: {
    root: '/help',
    quicklinks: '/help/quicklinks',
    trainingVideos: '/help/training-videos',
  },
  finances: {
    root: ROOTS.FINANCES,
    orgRoot: withParams(`${ROOTS.FINANCES}/:orgId`),
    contribution: {
      create: withParams(`${ROOTS.FINANCES}/:orgId/contribution/create`),
      edit: withParams(`${ROOTS.FINANCES}/:orgId/contribution/:transactionId/edit`),
    },
    expenditure: {
      create: withParams(`${ROOTS.FINANCES}/:orgId/expenditure/create`),
      edit: withParams(`${ROOTS.FINANCES}/:orgId/expenditure/:transactionId/edit`),
    },
    donors: `${ROOTS.FINANCES}/donors`,
    vendors: `${ROOTS.FINANCES}/vendors`,
    donorView: withParams(`${ROOTS.FINANCES}/donors/:donorId`),
    vendorView: withParams(`${ROOTS.FINANCES}/vendors/:vendorId`),
  },
  billing: {
    root: ROOTS.BILLING,
    transactions: `${ROOTS.BILLING}/transactions`,
    orgBillingDetails: `${ROOTS.BILLING}/org-billing-details`,
    taskBills: `${ROOTS.BILLING}/task-bill-runs`,
    taskBillView: withParams(`${ROOTS.BILLING}/task-bill-runs/:taskBillRunId`),
    paymentReminders: `${ROOTS.BILLING}/payment-reminders`,
  },
  adminTools: {
    manualTextMessages: {
      root: '/admin-tools/manual-text-messages',
      create: `/admin-tools/manual-text-messages/create`,
      view: withParams(`/admin-tools/manual-text-messages/:textMessageBatchId/view`),
    },
  },
  voterCommunications: {
    root: '/communications',
    general: '/communications/general',
    policyAnalyst: '/communications/policy-analyst',
    dataDirector: '/data-director',
  },
  downloads: {
    root: '/downloads',
  },
  uploads: {
    root: '/uploads',
    results: withParams('/uploads/:uploadId/results'),
    tagUpload: '/uploads/tags',
    contactUpload: '/uploads/contacts',
    volunteerUpload: '/uploads/volunteers',
    finalize: withParams('/uploads/:uploadId/finalize'),
    donors: '/uploads/donors',
    copyTag: '/uploads/copy-tag',
  },
  messageTemplates: {
    root: '/more/message-templates',
    messageTemplatesCreate: `/more/message-templates/create`,
    messageTemplatesAndSmsSurveyResults: `/more/message-templates/:templateId/edit`,
  },
  volunteers: {
    root: '/app-download',
  },
  machineApiKeys: {
    root: '/machine-to-machine-api',
  },
  voicemailSetup: {
    root: '/answering-machine-setup',
  },
  nearMeSettings: {
    root: '/near-me-settings',
  },
};

function withParams(route) {
  return {
    link: (params) => {
      let newRoute = route;
      for (let key in params) {
        newRoute = newRoute.replace(`:${key}`, String(params[key]));
      }
      return newRoute;
    },
  };
}
